import { createAction, createReducer } from '@reduxjs/toolkit'

import { gameInfoFetched } from 'store/auth'
import { graphicsJoinReceived } from 'store/graphics'
import { gameReceived } from 'store/user/game'
import { isAdminCreatedPlaylearningGame } from 'store/user/selectors'

const SUGGESTIONS_STORAGE_KEY = 'letsroll_robotutor_suggestions_enabled'
const SUGGESTIONS_PER_GAME_PREFIX = 'letsroll_robotutor_suggestions_game_'
const VOICEOVER_STORAGE_KEY = 'letsroll_robotutor_voiceover_enabled'
const MUSIC_STORAGE_KEY = 'letsroll_background_music_enabled'
const SKIP_TUTORIAL_STORAGE_KEY = 'letsroll_skip_tutorial_enabled'
const MUSIC_VOLUME_STORAGE_KEY = 'letsroll_background_music_volume'
const VOICEOVER_VOLUME_STORAGE_KEY = 'letsroll_robotutor_voiceover_volume'

const DEFAULT_MUSIC_VOLUME = 0.1
const DEFAULT_VOICEOVER_VOLUME = 0.2

const readGlobalSuggestionsEnabledFromStorage = () => {
  const stored = localStorage.getItem(SUGGESTIONS_STORAGE_KEY)
  if (stored === null) return true
  return stored === 'true'
}

const suggestionsStorageKeyForGame = (gameId) => `${SUGGESTIONS_PER_GAME_PREFIX}${gameId}`

/** Resolve enabled flag from localStorage rules for the loaded game (sync Redux after game loads). */
const readResolvedSuggestionsEnabledForGame = (game) => {
  if (!game?.id) return readGlobalSuggestionsEnabledFromStorage()
  if (isAdminCreatedPlaylearningGame(game)) {
    const stored = localStorage.getItem(suggestionsStorageKeyForGame(game.id))
    if (stored === null) return false
    return stored === 'true'
  }
  return readGlobalSuggestionsEnabledFromStorage()
}

const getInitialSuggestionsEnabled = () => readGlobalSuggestionsEnabledFromStorage()

const getInitialVoiceoverEnabled = () => {
  const stored = localStorage.getItem(VOICEOVER_STORAGE_KEY)
  if (stored === null) return true
  return stored === 'true'
}

const getInitialMusicEnabled = () => {
  const stored = localStorage.getItem(MUSIC_STORAGE_KEY)
  if (stored === null) return false
  return stored === 'true'
}

const getInitialSkipTutorialEnabled = () => {
  const stored = localStorage.getItem(SKIP_TUTORIAL_STORAGE_KEY)
  if (stored === null) return false
  return stored === 'true'
}

const getInitialMusicVolume = () => {
  const stored = localStorage.getItem(MUSIC_VOLUME_STORAGE_KEY)
  if (stored === null) return DEFAULT_MUSIC_VOLUME
  const parsed = parseFloat(stored)
  return Number.isFinite(parsed) ? Math.min(1, Math.max(0, parsed)) : DEFAULT_MUSIC_VOLUME
}

const getInitialVoiceoverVolume = () => {
  const stored = localStorage.getItem(VOICEOVER_VOLUME_STORAGE_KEY)
  if (stored === null) return DEFAULT_VOICEOVER_VOLUME
  const parsed = parseFloat(stored)
  return Number.isFinite(parsed) ? Math.min(1, Math.max(0, parsed)) : DEFAULT_VOICEOVER_VOLUME
}

const robotutorSuggestionsEnabledUpdated = createAction(
  'settings/robotutorSuggestionsEnabledUpdated'
)
const voiceoverToggled = createAction('settings/voiceoverToggled')
const musicToggled = createAction('settings/musicToggled')
const skipTutorialToggled = createAction('settings/skipTutorialToggled')
const musicVolumeChanged = createAction('settings/musicVolumeChanged')
const voiceoverVolumeChanged = createAction('settings/voiceoverVolumeChanged')

const initialState = {
  robotutorSuggestionsEnabled: getInitialSuggestionsEnabled(),
  robotutorVoiceoverEnabled: getInitialVoiceoverEnabled(),
  robotutorMusicEnabled: getInitialMusicEnabled(),
  skipTutorialEnabled: getInitialSkipTutorialEnabled(),
  musicVolume: getInitialMusicVolume(),
  voiceoverVolume: getInitialVoiceoverVolume()
}

const settingsReducer = createReducer(initialState, (builder) => {
  builder.addCase(robotutorSuggestionsEnabledUpdated, (state, action) => {
    state.robotutorSuggestionsEnabled = action.payload
  })

  builder.addCase(voiceoverToggled, (state, action) => {
    state.robotutorVoiceoverEnabled = action.payload
    localStorage.setItem(VOICEOVER_STORAGE_KEY, action.payload.toString())
  })

  builder.addCase(musicToggled, (state, action) => {
    state.robotutorMusicEnabled = action.payload
    localStorage.setItem(MUSIC_STORAGE_KEY, action.payload.toString())
  })

  builder.addCase(skipTutorialToggled, (state, action) => {
    state.skipTutorialEnabled = action.payload
    localStorage.setItem(SKIP_TUTORIAL_STORAGE_KEY, action.payload.toString())
  })

  builder.addCase(musicVolumeChanged, (state, action) => {
    state.musicVolume = action.payload
    localStorage.setItem(MUSIC_VOLUME_STORAGE_KEY, action.payload.toString())
  })

  builder.addCase(voiceoverVolumeChanged, (state, action) => {
    state.voiceoverVolume = action.payload
    localStorage.setItem(VOICEOVER_VOLUME_STORAGE_KEY, action.payload.toString())
  })

  builder.addCase(gameReceived, (state, action) => {
    const game = action.payload
    if (!game?.id) return
    state.robotutorSuggestionsEnabled = readResolvedSuggestionsEnabledForGame(game)
  })

  builder.addCase(gameInfoFetched, (state, action) => {
    const game = action.payload
    if (!game?.id) return
    state.robotutorSuggestionsEnabled = readResolvedSuggestionsEnabledForGame(game)
  })

  builder.addCase(graphicsJoinReceived, (state, action) => {
    const game = action.payload?.data?.game
    if (!game?.id) return
    state.robotutorSuggestionsEnabled = readResolvedSuggestionsEnabledForGame(game)
  })
})

export default settingsReducer

export const toggleRobotutorSuggestions = (enabled) => (dispatch, getState) => {
  const game = getState().user?.game?.data || getState().auth?.gameInfo
  if (isAdminCreatedPlaylearningGame(game)) {
    localStorage.setItem(suggestionsStorageKeyForGame(game.id), String(enabled))
  } else {
    localStorage.setItem(SUGGESTIONS_STORAGE_KEY, String(enabled))
  }
  dispatch(robotutorSuggestionsEnabledUpdated(enabled))
}

export const toggleRobotutorVoiceover = (enabled) => voiceoverToggled(enabled)
export const toggleRobotutorMusic = (enabled) => musicToggled(enabled)
export const toggleSkipTutorial = (enabled) => skipTutorialToggled(enabled)
export const setMusicVolume = (volume) => musicVolumeChanged(volume)
export const setVoiceoverVolume = (volume) => voiceoverVolumeChanged(volume)

export const selectRobotutorSuggestionsEnabled = (state) =>
  state.settings?.robotutorSuggestionsEnabled ?? true

export const selectRobotutorVoiceoverEnabled = (state) =>
  state.settings?.robotutorVoiceoverEnabled ?? true

export const selectRobotutorMusicEnabled = (state) => state.settings?.robotutorMusicEnabled ?? false

export const selectSkipTutorialEnabled = (state) => state.settings?.skipTutorialEnabled ?? false

export const selectMusicVolume = (state) => state.settings?.musicVolume ?? DEFAULT_MUSIC_VOLUME

export const selectVoiceoverVolume = (state) =>
  state.settings?.voiceoverVolume ?? DEFAULT_VOICEOVER_VOLUME
